import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Clock, CheckCircle, BookOpen, Target, Award, FileText, TrendingUp, Calculator, Layers, Lock, Unlock } from 'lucide-react';
import SEO from '../components/SEO';

const ISEMockExamPackSalesPage = () => {
  const [accessCode, setAccessCode] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleVerifyAccess = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/books/ise-mock-exam/check-access?token=${encodeURIComponent(accessCode)}&email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.valid) {
        setIsVerified(true);
      } else {
        setError('Invalid access code or email. Please check your credentials and try again.');
      }
    } catch (err) {
      setError('Unable to verify access. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const mockExams = [
    {
      id: 1,
      title: "Mock Exam 1",
      subtitle: "Foundation",
      focus: "Balanced intro covering all elements",
      accent: "#1e40af",
      accentLight: "#dbeafe",
      icon: BookOpen,
      questions: 100,
      difficulty: "Standard"
    },
    {
      id: 2,
      title: "Mock Exam 2",
      subtitle: "Scenarios",
      focus: "Case study heavy with real-world applications",
      accent: "#7c3aed",
      accentLight: "#ede9fe",
      icon: FileText,
      questions: 100,
      difficulty: "Intermediate"
    },
    {
      id: 3,
      title: "Mock Exam 3",
      subtitle: "Advanced",
      focus: "Hard difficulty for experienced candidates",
      accent: "#dc2626",
      accentLight: "#fee2e2",
      icon: Target,
      questions: 100,
      difficulty: "Advanced"
    },
    {
      id: 4,
      title: "Mock Exam 4",
      subtitle: "Calculations",
      focus: "Numerical focus and quantitative questions",
      accent: "#0d9488",
      accentLight: "#ccfbf1",
      icon: Calculator,
      questions: 100,
      difficulty: "Technical"
    },
    {
      id: 5,
      title: "Mock Exam 5",
      subtitle: "All Types",
      focus: "Comprehensive mix of all question types",
      accent: "#16a34a",
      accentLight: "#dcfce7",
      icon: Layers,
      questions: 100,
      difficulty: "Comprehensive"
    }
  ];

  const features = [
    { icon: FileText, title: "500 Exam Questions", description: "5 complete mock exams with 100 questions each" },
    { icon: Target, title: "Real Exam Format", description: "Matches actual ISE exam structure and timing" },
    { icon: TrendingUp, title: "Progressive Difficulty", description: "From foundation to advanced levels" },
    { icon: CheckCircle, title: "Instant Scoring", description: "Get immediate feedback on your performance" },
    { icon: Award, title: "Detailed Explanations", description: "Learn from every question with full rationales" },
    { icon: Shield, title: "CIRO Aligned", description: "Content matches official ISE syllabus" }
  ];

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISE Mock Exam Pack - Investment Services Exam Practice Tests",
    "description": "Complete ISE exam preparation with 5 full-length mock exams covering all 7 elements of the CIRO Investment Services Exam. 500 practice questions with detailed explanations.",
    "provider": {
      "@type": "Organization",
      "name": "Vitta Finance Academy",
      "sameAs": "https://vittafinance.com"
    },
    "offers": {
      "@type": "Offer",
      "price": "49.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT10H"
    }
  };

  return (
    <>
      <SEO 
        title="ISE Mock Exam Pack | 5 Full Practice Tests | 500 Questions | Vitta Finance"
        description="Prepare for the CIRO Investment Services Exam with 5 complete mock exams. 500 practice questions covering all 7 ISE elements with detailed explanations and instant scoring."
        keywords="ISE mock exam, ISE practice test, Investment Services Exam questions, CIRO ISE prep, ISE exam simulator, ISE practice questions"
      />
      
      <script type="application/ld+json">
        {JSON.stringify(jsonLdSchema)}
      </script>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)',
        padding: '80px 20px',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(212, 175, 55, 0.2)',
            border: '1px solid #d4af37',
            borderRadius: '20px',
            padding: '6px 16px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#d4af37',
            marginBottom: '20px',
            letterSpacing: '0.5px'
          }}>
            INVESTMENT SERVICES EXAM
          </div>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '700',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            ISE Mock Exam Pack
          </h1>
          <p style={{
            fontSize: '1.2rem',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.6'
          }}>
            Master the CIRO Investment Services Exam with 5 comprehensive practice tests. 
            500 questions covering all 7 elements with detailed explanations.
          </p>
          <div style={{
            display: 'flex',
            gap: '30px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: '30px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37' }}>500</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Practice Questions</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37' }}>5</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Mock Exams</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37' }}>7</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>ISE Elements</div>
            </div>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {!isVerified ? (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <Lock size={28} color="white" />
                </div>
                <h2 style={{ fontSize: '1.5rem', color: '#0f172a', marginBottom: '10px' }}>
                  Access Your Mock Exams
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                  Enter your access code and email to unlock all 5 ISE mock exams
                </p>
              </div>

              <form onSubmit={handleVerifyAccess}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="ise-mock-email-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '1rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Access Code
                  </label>
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter your access code"
                    required
                    data-testid="ise-mock-access-code-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '1rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {error && (
                  <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    marginBottom: '20px'
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="ise-mock-verify-btn"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'white',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: isLoading ? 'wait' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {isLoading ? 'Verifying...' : 'Unlock Mock Exams'}
                </button>
              </form>

              <div style={{
                marginTop: '30px',
                paddingTop: '30px',
                borderTop: '1px solid #e2e8f0',
                textAlign: 'center'
              }}>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '15px' }}>
                  Need access? Purchase the ISE Mock Exam Pack
                </p>
                <a
                  href="https://vittafinance.mykajabi.com/offers/idimsmPX"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ise-mock-purchase-btn"
                  style={{
                    display: 'inline-block',
                    padding: '12px 24px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    color: '#1e3a8a',
                    background: '#d4af37',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    transition: 'transform 0.2s'
                  }}
                >
                  Purchase for CAD $49.99
                </a>
              </div>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '2px solid #16a34a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Unlock size={28} color="white" />
              </div>
              <h2 style={{ fontSize: '1.5rem', color: '#0f172a', marginBottom: '10px' }}>
                Access Granted
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                Your mock exams are now unlocked. Scroll down to begin practicing.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Mock Exams Grid */}
      <section style={{ padding: '60px 20px', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '1.75rem',
            color: '#0f172a',
            marginBottom: '40px'
          }}>
            5 Complete Mock Exams
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {mockExams.map((exam) => {
              const IconComponent = exam.icon;
              return (
                <div
                  key={exam.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: `2px solid ${isVerified ? exam.accent : '#e2e8f0'}`,
                    padding: '28px',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: isVerified ? 'pointer' : 'default',
                    opacity: isVerified ? 1 : 0.7
                  }}
                  onMouseOver={(e) => {
                    if (isVerified) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: exam.accentLight,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconComponent size={24} color={exam.accent} />
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#0f172a',
                        margin: 0
                      }}>
                        {exam.title}
                      </h3>
                      <span style={{
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: exam.accent
                      }}>
                        {exam.subtitle}
                      </span>
                    </div>
                  </div>

                  <p style={{
                    fontSize: '0.9rem',
                    color: '#64748b',
                    marginBottom: '16px',
                    lineHeight: '1.5'
                  }}>
                    {exam.focus}
                  </p>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '16px',
                    borderTop: '1px solid #f1f5f9'
                  }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {exam.questions} Questions
                      </span>
                      <span style={{
                        fontSize: '0.8rem',
                        color: exam.accent,
                        fontWeight: '600'
                      }}>
                        {exam.difficulty}
                      </span>
                    </div>
                    {isVerified ? (
                      <Link
                        to={`/ise-mock-exam/${exam.id}`}
                        data-testid={`ise-mock-exam-${exam.id}-link`}
                        style={{
                          padding: '8px 16px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: 'white',
                          background: exam.accent,
                          borderRadius: '6px',
                          textDecoration: 'none',
                          transition: 'opacity 0.2s'
                        }}
                      >
                        Start Exam
                      </Link>
                    ) : (
                      <Lock size={18} color="#94a3b8" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '1.75rem',
            color: '#0f172a',
            marginBottom: '40px'
          }}>
            What's Included
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <IconComponent size={20} color="white" />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#0f172a',
                      marginBottom: '6px'
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      fontSize: '0.9rem',
                      color: '#64748b',
                      margin: 0,
                      lineHeight: '1.5'
                    }}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.75rem', marginBottom: '15px' }}>
            Ready to Ace the ISE Exam?
          </h2>
          <p style={{ opacity: '0.9', marginBottom: '25px', fontSize: '1.05rem' }}>
            Get instant access to all 5 mock exams with 500 practice questions
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://vittafinance.mykajabi.com/offers/ISEMockExamPack"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '14px 28px',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#1e3a8a',
                background: '#d4af37',
                borderRadius: '8px',
                textDecoration: 'none'
              }}
            >
              Get Mock Exam Pack - CAD $49.99
            </a>
            <Link
              to="/ise-study-guide"
              style={{
                padding: '14px 28px',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                background: 'transparent',
                border: '2px solid white',
                borderRadius: '8px',
                textDecoration: 'none'
              }}
            >
              View ISE Study Guide
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ISEMockExamPackSalesPage;
